<template>
  <v-menu
    v-if="hasChatAccess || hasNotificationAccess"
    offset-x
    :nudge-right="16"
    :close-on-content-click="false"
    transition="slide-x-transition"
    :min-width="430"
    :max-width="430"
  >
    <template #activator="{ on }">
      <NavItem
        icon="notifications"
        :data-test="`nav-tab-${label}`"
        :label="label"
        :badge="totalUnreadCount"
        v-on="on"
      />
    </template>

    <v-card>
      <v-tabs v-model="tabs" dark grow slider-color="amber">
        <v-tab v-for="{ href, name } in tabItems" :key="name" :href="href">
          {{ name }}
          <template v-if="showUnreadCountForTab(name)">
            &nbsp;
            <UnreadCount :unread-count="totalUnreadCount" tab />
          </template>
        </v-tab>
        <v-tab key="Settings" href="#tab-settings">Settings</v-tab>
      </v-tabs>
      <v-tabs-items v-model="tabs">
        <v-tab-item key="Messages" value="tab-messages">
          <ChannelsNotificationHub />
        </v-tab-item>
        <v-tab-item key="Job Updates" value="tab-jobUpdates">
          <JobUpdates />
        </v-tab-item>
        <v-tab-item key="Settings" value="tab-settings">
          <NotificationSettings />
        </v-tab-item>
      </v-tabs-items>
    </v-card>
  </v-menu>
</template>

<script setup lang="ts">
import ChannelsNotificationHub from './ChannelsNotificationHub.vue'
import JobUpdates from './JobUpdates.vue'
import NotificationSettings from './Settings.vue'
import UnreadCount from './UnreadCount.vue'
import NavItem from '../nav/NavItem.vue'
import { useChatStore } from '@/components/common/chat/useChatData'
import { computed, ref } from 'vue'
import { useGetters } from 'vuex-composition-helpers'

type TabType = 'tab-messages' | 'tab-jobUpdates' | 'tab-settings'
const tabs = ref<TabType>('tab-messages')

const store = useChatStore()

const { hasChatAccess, hasNotificationAccess } = useGetters('permissions', [
  'hasChatAccess',
  'hasNotificationAccess',
])

const label = computed(() => {
  return hasChatAccess.value ? 'Messages & Alerts' : 'Alerts'
})

const totalUnreadCount = computed(() => {
  const count = hasChatAccess.value ? visibleChatUnreadCount.value : 0
  return count > 99 ? '99+' : count
})

const tabItems = computed(() => {
  const items: any[] = []

  if (hasChatAccess.value) {
    items.push({ href: '#tab-messages', name: 'Messages' })
  }

  if (hasNotificationAccess.value) {
    items.push({ href: '#tab-jobUpdates', name: 'Job Updates' })
  }

  return items
})

const visibleChatUnreadCount = computed(() => {
  return store.filteredChannels
    .map((channel) => channel.unreadCount || 0)
    .reduce((accumulator, currentValue) => accumulator + currentValue, 0)
})

const showUnreadCountForTab = (name: string) => {
  return name === 'Messages' && visibleChatUnreadCount.value > 0
}
</script>

<style scoped>
.total-unread-count {
  font-size: 13px;
}
</style>
